import { isClosedStatus, type UserTask } from "@/types/userTasks.ts";

type DueTask = Pick<UserTask, "dueMs" | "status" | "dueDate">;

/** A local `YYYY-MM-DD`, to compare with a task's due day. */
function localDay(ms: number): string {
  const d = new Date(ms);
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

/**
 * The day a task is due on when it has no time: the server's `dueDate`, which
 * is the account's day. An older server sends none; then a due time at local
 * midnight stands for a date.
 */
function dueDay(task: DueTask): string | undefined {
  if (task.dueDate) return task.dueDate;
  if (task.dueMs === null) return undefined;
  const due = new Date(task.dueMs);
  return due.getHours() === 0 && due.getMinutes() === 0 ? localDay(task.dueMs) : undefined;
}

/**
 * When a task becomes late, in the viewer's time: the end of its due day for
 * a date with no time, or its due time. Null with no due date.
 */
export function dueDeadlineMs(task: Pick<DueTask, "dueMs" | "dueDate">): number | null {
  if (task.dueMs === null) return null;
  const day = dueDay({ ...task, status: "open" });
  if (!day) return task.dueMs;
  const end = new Date(`${day}T00:00:00`);
  end.setDate(end.getDate() + 1);
  return end.getTime();
}

/**
 * When a task is due, as a `Date` to format: noon of its due day for a date
 * with no time, so no browser timezone moves it to another day.
 */
export function dueDisplayDate(task: DueTask): Date | null {
  if (task.dueMs === null) return null;
  const day = dueDay(task);
  return day ? new Date(`${day}T12:00:00`) : new Date(task.dueMs);
}

/**
 * A task's due date, as its row says it: "Due Fri, Oct 9, 5:00 PM", or "Due
 * Sun, Nov 1" for a date with no time — an imported list gives a date only.
 * A date with no time is due all that day. None for a closed task, or one with
 * no date.
 *
 * Past due it says so in words, so the state reaches a screen reader and a
 * reader who takes in weight less readily than wording.
 *
 * `label` and `when` are the sentence in its two parts, because the surfaces
 * draw them differently — the label carries the emphasis and the date is read
 * at the weight of any other subtext. `text` is still the whole sentence, for
 * an accessible name, which wants it said rather than shown.
 */
export function dueLine(
  task: DueTask,
  nowMs: number,
  locale?: string,
): { text: string; label: string; when: string; overdue: boolean } | null {
  if (task.dueMs === null || isClosedStatus(task.status)) return null;
  const day = dueDay(task);
  const shown = dueDisplayDate(task) ?? new Date(task.dueMs);
  const overdue = day ? localDay(nowMs) > day : task.dueMs <= nowMs;
  const when = shown.toLocaleString(locale, {
    // Spelled out once the date has passed: an overdue row is scanned in a
    // hurry, and "Monday" lands where "Mon" has to be decoded.
    weekday: overdue ? "long" : "short",
    month: "short",
    day: "numeric",
    ...(day ? {} : { hour: "numeric", minute: "2-digit" }),
  });
  const label = overdue ? "Past due:" : "Due:";
  return { text: `${label} ${when}`, label, when, overdue };
}

/**
 * The same date, short enough to sit at the end of a single-line row.
 *
 * "Oct 15" rather than "Past due: Thursday, Oct 15 at 8:21 PM". A compact row
 * gives the date whatever the title does not need, and the full sentence took
 * so much of it that a title could be cut to two words — which inverts what
 * the row is for. The weekday and the time are what a reader opens the task
 * for; the month and day are what they scan down the column.
 *
 * `spoken` keeps the full sentence for the accessible name. The short form
 * drops the words "past due" along with everything else, and the heavier
 * weight is the only thing left saying so — which is the one reader it would
 * not reach.
 */
export function dueShort(
  task: DueTask,
  nowMs: number,
  locale?: string,
): { text: string; spoken: string; overdue: boolean } | null {
  const full = dueLine(task, nowMs, locale);
  const shown = dueDisplayDate(task);
  if (!full || !shown) return null;
  return {
    text: shown.toLocaleString(locale, { month: "short", day: "numeric" }),
    spoken: full.text,
    overdue: full.overdue,
  };
}
